import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { AnalyticsEventName } from '@/api/analytics';
import { confirmationEmailPost } from '@/api/auth';
import { ButtonClose } from '@/components/buttons';
import { showTooltip } from '@/helpers/tooltip';
import { useAnalytics } from '@/hooks/analytics';
import useEventListener from '@/hooks/eventListener';
import { useSwrMutation } from '@/hooks/swr';
import { STORAGE, useCommonStore, useLocalStorageStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScEmailConfirmPopup } from './styled';
import { ScContainer } from '@/styles/common';

export const EmailConfirmPopup = () => {
  const { t } = useTranslation('common');
  const { sendEventToAnalytics } = useAnalytics();

  const {
    user,
    setEmailConfirmationPopupSeen,
    setEmailConfirmationPopupHeight,
  } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      setEmailConfirmationPopupSeen: state.setEmailConfirmationPopupSeen,
      setEmailConfirmationPopupHeight: state.setEmailConfirmationPopupHeight,
    }))
  );

  const lastSeenEmailConfirmationPopup = useLocalStorageStore
    .getState()
    .get(STORAGE.LAST_SEEN_EMAIL_CONFIRMATION_POPUP);

  const { trigger: requestsConfirmationEmail } = useSwrMutation<null>({
    key: 'confirmationEmailPost',
    fetcher: confirmationEmailPost,
    options: { populateCache: true },
  });

  const ref = useRef<HTMLDivElement>(null);

  const handleLinkClick = useCallback(async () => {
    if (!user) return;

    const response = await requestsConfirmationEmail(user.email);

    if (!response) return;

    setEmailConfirmationPopupSeen();

    showTooltip({
      type: TooltipTypes.SUCCESS,
      body: t('verifyEmailPopup.successTooltip'),
    });
  }, [requestsConfirmationEmail, setEmailConfirmationPopupSeen, t, user]);

  useEffect(() => {
    sendEventToAnalytics({
      name:
        lastSeenEmailConfirmationPopup == null
          ? AnalyticsEventName.EMAIL_CONFIRMATION_POPUP__FIRST_APPEARANCE
          : AnalyticsEventName.EMAIL_CONFIRMATION_POPUP__SUBSEQUENT_APPEARANCE,
    });
  }, [sendEventToAnalytics, lastSeenEmailConfirmationPopup]);

  useEventListener(
    'resize',
    () =>
      ref.current && setEmailConfirmationPopupHeight(ref.current.clientHeight),
    undefined,
    true
  );

  return (
    <ScEmailConfirmPopup ref={ref}>
      <ScContainer relative>
        <div className="text">
          {t('verifyEmailPopup.text')}
          <span className="link" onClick={handleLinkClick}>
            {t('verifyEmailPopup.linkText')}
          </span>
        </div>
        <ButtonClose
          isStatic
          dark
          small
          onClick={setEmailConfirmationPopupSeen}
        />
      </ScContainer>
    </ScEmailConfirmPopup>
  );
};
